import { AionzSnackbar, EmptyContentComponent, LoadingService } from '@aionzdev/design-system';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, finalize, of, switchMap, tap } from 'rxjs';
import { LoadState, PurchaseRequestStatus } from '../../../enums';
import { PurchaseRequestAttachment } from '../../../interfaces/purchase-request-attachment';
import { PurchaseRequestAttachmentGroup } from '../../../interfaces/purchase-request-attachment-group';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { AttachmentGroupComponent } from '../attachment-group/attachment-group.component';

@Component({
  selector: 'aionz-purchase-request-attachments',
  imports: [EmptyContentComponent, AttachmentGroupComponent],
  templateUrl: './purchase-request-attachments.component.html',
  styleUrl: './purchase-request-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestAttachmentsComponent {
  public purchaseId = input.required<string>();
  public status = input<PurchaseRequestStatus>();

  protected readonly attachmentLoadState = signal<LoadState>(LoadState.IDLE);
  protected readonly attachmentGroups = signal<PurchaseRequestAttachmentGroup[]>([]);
  protected readonly attachmentsErrorMessage = signal('');
  
  protected readonly LoadState = LoadState;

  protected readonly hasAttachments = computed(() => this.attachmentGroups().length > 0);
  protected readonly isAttachmentsEmpty = computed(
    () => this.attachmentLoadState() === LoadState.LOADED && !this.hasAttachments()
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly loadingService = inject(LoadingService);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly snackBar = inject(AionzSnackbar);

  constructor() {
    combineLatest([toObservable(this.purchaseId), toObservable(this.status)])
      .pipe(
        tap(() => {
          this.attachmentLoadState.set(LoadState.LOADING);
          this.attachmentsErrorMessage.set('');
        }),
        switchMap(([id, status]) => {
          if (!id) return of(null);
          return this.purchasingService.findPurchaseRequestAttachments(id, status).pipe(
            catchError(() => {
              this.attachmentLoadState.set(LoadState.ERROR);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((response) => {
        if (response) {
          this.attachmentGroups.set(response.body || []);
          this.attachmentLoadState.set(LoadState.LOADED);
        }
      });
  }

  protected downloadAttachment(attachment: PurchaseRequestAttachment): void {
    this.loadingService.showLoading();
    this.purchasingService
      .downloadPurchaseRequestAttachment(this.purchaseId(), attachment.id)
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const anchor = this.document.createElement('a');
          anchor.href = url;
          anchor.download = attachment.fileName;
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackBar.error('Erro ao baixar anexo.');
        }
      });
  }
}
